import { Injectable, inject } from '@angular/core';
import { OperationService } from 'src/app/shared/services/step.service';
import { IOcrConf, IOcrManualConf } from 'src/app/user-identification/models/ocr';
import { IDocConf, IDocsConf } from 'src/app/user-identification/models/documents-check';
import { ILifeTest, IPolConf } from 'src/app/user-identification/models/life-check';
import { getFormSteps } from 'src/app/user-identification/utils/form-operation.util';
import {
	IFormOperation,
	IOperation,
	MODE,
	OPERATION_TYPE,
	TestStatus,
} from 'src/app/user-identification/models/user-identification';

type Operation = { name: OPERATION_TYPE; steps: number };

export interface IFormProgressConf {
	formStepIndex: number;
}

type ProgressConf = IPolConf | IOcrConf | IOcrManualConf | IDocsConf | IFormProgressConf;

@Injectable({
	providedIn: 'root',
})
export class ProgressService {
	// #region Services
	private operationService = inject(OperationService);
	// #endregion

	private operations: IOperation[];

	constructor() {
		this.operationService.getOperations().subscribe({
			next: (response: IOperation[]) => {
				this.operations = response;
			},
		});
	}

	public getValue(conf?: ProgressConf): number {
		if (this.operations?.length) {
			const operations: Operation[] = [];
			// Definimos para cada operación los pasos que tiene
			this.operations.forEach((op: IOperation) => {
				if (op.type === OPERATION_TYPE.HOME) {
					operations.push({ name: op.type, steps: 1 });
				}
				if (op.type === OPERATION_TYPE.OCR) {
					operations.push({ name: op.type, steps: 2 });
				}
				if (op.type === OPERATION_TYPE.POL) {
					if (op.config.mode === MODE.MANUAL) {
						operations.push({ name: op.type, steps: 1 });
					}
					// El +1 es por la fase de centrado
					if (op.config.mode === MODE.AUTO) {
						operations.push({ name: op.type, steps: (op.config?.setting?.total_test ?? 2) + 1 });
					}
				}
				if (op.type === OPERATION_TYPE.DOCUMENTS) {
					// Solo contar documentos requeridos para el progreso
					const requiredDocsCount =
						op.config?.docs?.filter((doc) => doc.required).length || op.config?.docs?.length || 1;
					operations.push({ name: op.type, steps: requiredDocsCount });
				}
				if (op.type === OPERATION_TYPE.CHOOSE) {
					operations.push({ name: op.type, steps: 1 });
				}
				if (op.type === OPERATION_TYPE.CONNECTIONS) {
					operations.push({ name: op.type, steps: 1 });
				}
				if (op.type === OPERATION_TYPE.FORM) {
					const formOperation = op as IFormOperation;
					operations.push({
						name: op.type,
						steps: getFormSteps(formOperation.config).length || 1,
					});
				}
			});
			const totalSteps = operations.reduce((acc, m) => acc + m.steps, 0);
			const stepSize = 100 / totalSteps;
			const operationInProgress: IOperation = this.operations.find((op: IOperation) => !op.completed);

			let progress = 0;

			// Para indicar en que paso estamos dentro de la operación en curso
			for (const ope of operations) {
				if (ope.name === operationInProgress.type) {
					// estamos en este módulo → sumamos los pasos que llevamos
					let currentStep = 0;
					if (operationInProgress.type === OPERATION_TYPE.OCR) {
						// Empezando prueba
						// Por defecto estamos en el primer paso. Solo pasamos a 2 si la conf
						// indica explícitamente que el primer lado está completo.
						currentStep = 1;
						const ocrConf = conf as IOcrConf | undefined;
						if (ocrConf?.document?.length && ocrConf.document[0]?.status === TestStatus.OnComplete) {
							// Primer lado completado
							currentStep = 2;
						}
					}
					if (operationInProgress.type === OPERATION_TYPE.POL) {
						// Empezando prueba
						if ((conf as IPolConf)?.middleFace.status !== TestStatus.OnComplete) {
							currentStep = 1;
						} else {
							// Centrado completado
							const indexTest = (conf as IPolConf)?.lifeTest.findIndex(
								(test: ILifeTest) => test.status !== TestStatus.OnComplete,
							);
							if (indexTest > -1) {
								currentStep = indexTest + 2; // +2 por la fase de centrado y el primer test
							} else {
								currentStep = 1;
							}
						}
					}
					if (operationInProgress.type === OPERATION_TYPE.DOCUMENTS) {
						// Contar documentos completados (solo documentos requeridos van a estar OnComplete)
						const completedDocs =
							(conf as IDocsConf)?.documents?.filter(
								(doc: IDocConf) => doc.status === TestStatus.OnComplete,
							).length || 0;

						// Total de documentos requeridos
						const totalRequiredDocs =
							operationInProgress.config?.docs?.filter((doc) => doc.required).length ||
							operationInProgress.config?.docs?.length ||
							1;

						// currentStep va de 1 a totalRequiredDocs+1
						currentStep = Math.min(completedDocs + 1, totalRequiredDocs + 1);
					}
					if (operationInProgress.type === OPERATION_TYPE.FORM) {
						const formConf = conf as IFormProgressConf | undefined;
						currentStep = (formConf?.formStepIndex ?? 0) + 1;
					}
					progress += (currentStep - 1) * stepSize;
					break;
				} else {
					// operación anterior → sumamos todos sus pasos
					progress += ope.steps * stepSize;
				}
			}

			return progress;
		}
		return 0;
	}
}
